<template>
<div class="diagnostic-page">
  <h1 class="page-title">
    <span>Diagnostic</span>
  </h1>


<b-tabs>
  <b-tab title="Active Issues" active @click="updateActiveIssues">
    <template v-slot:title>
      <h6>Active Issues: <b-badge>{{ activeIssues.length }}</b-badge></h6>
    </template>
    <ResponsiveTable
        :items="activeIssues"
        :fields="activeIssuesFields"
        :searchable_fields="activeIssuesSearchableFields"
        sort-by="since"
        sort-direction="desc"
    >
        <template v-slot:cell(product_serial_num)="row">
          <b-icon icon="circle-fill" :variant="row.item.online ? 'success' : 'danger'"/>
          &nbsp;
          <router-link :to="{ name: 'DevicePage', params: { serial: row.item.serial, fromDevicePage: true }}">{{ row.item.product_serial_num }}</router-link>
        </template>
        <template v-slot:cell(circuit)="row">
          <router-link :to="{ name: 'DashboardPage' }">
            <span @click="setSelectedDevice(row.item.product_serial_num)">
                {{row.item.circuit}}
            </span>
          </router-link>
        </template>
        <template v-slot:cell(since)="row">
          {{ datetimeFormatter(row.item.since) }} (<timeago :datetime=row.item.since></timeago>)
        </template>
      </ResponsiveTable>
  </b-tab>
  <b-tab title="Historic Issues" lazy @click="updateHistoricIssues">
    <template v-slot:title>
      <h6>Historic Issues: <b-badge>{{ historicIssuesCount }}</b-badge></h6>
    </template>
    <div v-if="isFetching" class="d-flex justify-content-center align-items-center" style="height:  90vh;">
          <b-spinner style="width: 10rem; height: 10rem;"></b-spinner>
        </div>

    <div v-else>
      <b-table  striped hover :items="historicIssues" :fields="historicIssuesFields">
        <template v-slot:cell(product_serial_num)="row">
          <router-link :to="{ name: 'DevicePage', params: { serial: row.item.serial, fromDevicePage: true }}">{{ row.item.product_serial_num }}</router-link>
        </template>
        <template v-slot:cell(start)="row">
          {{ datetimeFormatter(row.item.start) }} (<timeago :datetime=row.item.start></timeago>)
        </template>
        <template v-slot:cell(end)="row">
          {{ datetimeFormatter(row.item.end) }} (<timeago :datetime=row.item.end></timeago>)
        </template>
      </b-table>
      <b-pagination
      v-model="historicIssuescurrentPage"
      :total-rows="historicIssuesCount"
      :per-page="25"
      first-text="First"
      prev-text="Prev"
      next-text="Next"
      last-text="Last"
      limit="9"
      @change="changeHistoricIssuesPage"
    ></b-pagination>
    </div>
  </b-tab>
  <b-tab title="Calibration Issues" lazy @click="updateCalibrationIssues">
    <template v-slot:title>
      <h6>Calibration Issues: <b-badge>{{ calibrationIssuesCount }}</b-badge></h6>
    </template>
    <ResponsiveTable
        :items="calibrationIssues"
        :fields="calibrationIssuesFields"
        :searchable_fields="calibrationIssuesSearchableFields"
        sort-by="since"
        sort-direction="desc"
    >
        <template v-slot:cell(product_serial_num)="row">
          <b-icon icon="circle-fill" :variant="row.item.online ? 'success' : 'danger'"/>
          &nbsp;
          <router-link :to="{ name: 'DevicePage', params: { serial: row.item.serial, fromDevicePage: true }}">
            {{ row.item.product_serial_num }}
          </router-link>
        </template>

        <template v-slot:cell(circuit)="row">
          <router-link :to="{ name: 'DashboardPage' }">
            <span @click="setSelectedDevice(row.item.product_serial_num)">
                {{ row.item.circuit_name }}
            </span>
          </router-link>
        </template>

         <template v-slot:cell(tdr_calibration_time)="row">
            <span v-if="row.item.tdr_calibration_time">
              {{ datetimeFormatter(row.item.tdr_calibration_time) }} (<timeago :datetime=row.item.tdr_calibration_time></timeago>)
            </span>
        </template>

        <template v-slot:cell(action)="row">
          <b-button
            v-if="$base.isAllowed('forceCalibrate') && row.item.online"
            :disabled="row.item.tdr_calibration_status === 'calibrated'"
            id="calibrate_button"
            v-b-tooltip.hover
            :title="row.item.tdr_calibration_status === 'calibrated' ? 'Device already calibrated' : 'Start calibration for this device'"
            v-b-modal="'calibrationModal'"
            variant="primary"
            size="sm"
            @click.prevent="setSelectedDeviceForCalibration(row.item)"
          >Calibrate</b-button>
        </template>
    </ResponsiveTable>
  </b-tab>
</b-tabs>

<CalibrationModal :device=deviceToCalibrate></CalibrationModal>

</div>
</template>

<script>
import {
  mapActions, mapState
} from 'vuex';
import { diagnosticService, deviceService } from "@/_services";
import datetimeFormatter from '@/core/datetimeFormatterHelper';
import ResponsiveTable from "@/components/ResponsiveTable/ResponsiveTable";
import CalibrationModal from '../../components/CalibrationModal/CalibrationModal.vue';

export default {
  name: 'Diagnostic',
  components: {ResponsiveTable, CalibrationModal},
  data() {
    return {
      activeIssuesFields: [{
          key: 'product_serial_num',
          label: 'SERIAL',
          class: 'productSerialNum',
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: 'circuit',
          label: 'CIRCUIT',
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: 'pole_substation_name',
          label: 'SUBSTATION NAME',
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: 'type',
          label: 'ISSUE',
          sortable: true,
          sortDirection: 'asc',
          formatter: issue_wording_formatter
        },
        {
          key: 'since',
          label: 'SINCE',
          sortable: true,
          sortDirection: 'asc',
          formatter: datetimeFormatter
        }
      ],
      activeIssuesSearchableFields: [
          "product_serial_num",
          "pole_substation_name",
          "circuit",
          "type",
      ],
      activeIssues: [],
      historicIssuesFields: [{
          key: 'product_serial_num',
          label: 'SERIAL',
          class: 'productSerialNum',
        },
        {
          key: 'pole_substation_name',
          label: 'SUBSTATION NAME',
        },
        {
          key: 'circuit',
          label: 'CIRCUIT',
        },
        {
          key: 'type',
          label: 'ISSUE',
          formatter: issue_wording_formatter
        },
        {
          key: 'start',
          label: 'START',
          formatter: datetimeFormatter
        },
        {
          key: 'end',
          label: 'END',
          formatter: datetimeFormatter
        }
      ],
      historicIssues: [],
      historicIssuesCount: 0,
      historicIssuescurrentPage: 1,
      isFetching: true,
      deviceToCalibrate: null,
      calibrationIssues: [],
      calibrationIssuesFields:[
        {
          key: "product_serial_num",
          label: "PRODUCT SERIAL NUMBER",
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: "circuit_name",
          label: "CIRCUIT",
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: "tdr_calibration_status",
          label: "CALIBRATION STATUS",
          formatter: calibration_status_formatter,
          sortable: true,
          sortDirection: 'asc',
        },
        {
          key: 'tdr_calibration_time',
          label: 'CALIBRATION TIME',
          sortable: true,
          sortDirection: 'desc',
        },
        {
          key: "action",
          label: "ACTION",
          sortable: false,
        },
      ],
       calibrationIssuesSearchableFields:[
        "product_serial_num",
        "circuit_name",
        "tdr_calibration_status"
      ],
    }
  },
  computed: {
    ...mapState("layout", {
      hideInactiveCircuits: state => state.hideInactiveCircuits
    }),
    calibrationIssuesCount() {
        return this.calibrationIssues.length;
    },
  },
  watch: {
    hideInactiveCircuits() {
      this.updateActiveIssues()
    }
  },
  methods: {
    ...mapActions('alert', ['success', 'error']),
    ...mapActions("dashboard", ["setSelectedDevice",]),
    updateActiveIssues() {
      diagnosticService.getActiveIssues(this.hideInactiveCircuits)
      .then(response => {
        this.activeIssues = response.data.device_issues;
      })
      .catch(() => {
        this.error('Error getting active issues');
      });
    },
    updateHistoricIssues() {
      return diagnosticService.getHistoricIssues(this.historicIssuescurrentPage)
        .then(response => {
          this.historicIssues = response.data.device_issues;
          this.historicIssuesCount = response.data.total;
        })
        .catch(() => {
          this.error('Error getting historic issues');
        });
    },
    updateCalibrationIssues() {
      deviceService.findAll(this.hideInactiveCircuits)
        .then(response => {
          this.calibrationIssues = response.data.devices.map(device => ({
            ...device,
            circuit_name: device.pole && device.pole.circuit_name ? device.pole.circuit_name : ''
          }));
        })
        .catch(() => {
          this.error('Error getting calibration issues');
        });
    },
    setSelectedDeviceForCalibration(device){
      this.deviceToCalibrate = device;
    },
    async changeHistoricIssuesPage(page) {
      this.isFetching = true
      this.historicIssuescurrentPage = page;
      await this.updateHistoricIssues();
      this.isFetching = false
    },
    datetimeFormatter(date) {
      return datetimeFormatter(date);
    },
  },
  mounted() {
    this.updateActiveIssues();
    this.updateHistoricIssues();
    this.updateCalibrationIssues();
    this.isFetching = false
  }
};

function issue_wording_formatter(issue) {
  const issue_wording = {
    TEMPERATURE_EXCEEDED: "Temperature exceeded",
    BATTERY_LOW_LEVEL: "Low Battery",
    OFFLINE: "Not Connected",
    MAINS_NO_VOLTAGE: "Loss of Power"
  };
  return (issue in issue_wording) ? issue_wording[issue] : issue;
}

// value is the field value, item is the whole row
function calibration_status_formatter(value, key, item) {
  if (!item) return '';
  if ((item.tdr_calibration_status || '').toUpperCase() === 'UNCALIBRATED') {
    if (!item.tdr_calibration_time) {
      return 'Never Calibrated';
    } else {
      return `Failed at ${datetimeFormatter(item.tdr_calibration_time)}`;
    }
  }
  return 'Calibrated';
}

</script>

<style src="./Diagnostic.scss" lang="scss" />
